import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { DsMessageModule } from '@bmw-ds/components';

import { SearchCustomerRequestType } from '../../../customer/services/search.service';
import { useI18n } from '../../../i18n/services/i18n.service';
import {
  isSearchRequestWarning,
  RequestWarningType,
  SearchRequestWarningType,
} from '../../../utils/requests/RequestWarning';

@Component({
  selector: 'cdf-customer-search-message',
  imports: [DsMessageModule],
  host: {
    '[class.d-none]': 'translatableWarnings().length === 0 && failedRequests().size === 0',
  },
  template: `
    <div class="d-flex flex-column gap-2x">
      @for (warning of translatableWarnings(); track warning) {
        <ds-message [messageText]="i18n.enum(warning, 'searchResults.warnings')" tone="caution" />
      }
      @if (allBackendsFailed()) {
        <ds-message [messageText]="i18n('searchResults.fullBackendFailure')" tone="critical" />
      } @else if (singleBackendFailure()) {
        <ds-message
          [messageText]="i18n('searchResults.singleBackendFailure', { backend: singleBackendFailure() })"
          tone="caution"
        />
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerSearchMessageComponent {
  protected readonly i18n = useI18n();

  readonly failedRequests = input.required<Set<SearchCustomerRequestType>>();
  readonly attemptedRequests = input.required<Set<SearchCustomerRequestType>>();
  readonly warnings = input.required<Set<RequestWarningType>>();

  /** Every backend that was actually queried failed - no result can be trusted.
   * The vehicle search skips ESO CRM outside GB and IE (CUSTAPP-607), so a single
   * failure can already be a complete failure.
   */
  protected readonly allBackendsFailed = computed(
    () => this.failedRequests().size > 0 && this.failedRequests().size >= this.attemptedRequests().size,
  );
  protected readonly translatableWarnings = computed<SearchRequestWarningType[]>(() =>
    [...this.warnings()].filter(isSearchRequestWarning),
  );

  protected readonly singleBackendFailure = computed(() =>
    !this.allBackendsFailed() && this.failedRequests().size === 1 ? [...this.failedRequests()][0] : undefined,
  );
}
